import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ProfileSection from '../components/Profile/ProfileSection';
import feather from 'feather-icons';
import ProfileHeader from '../components/Profile/ProfileHeader';
import BottomNavbar from '../components/Home/BottomNavbar';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import Alert from '../components/Alert';
import Loader from '../components/Loader';
import { useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';
import useRefreshSignal from '../hooks/useRefreshSignal';
import '../styles/ProfilePage.css';

function Profile() {
    const navigate = useNavigate();
    const refreshSignal = useOutletRefreshSignal();
    const { t, language } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);

    const fetchUser = useCallback(async () => {
        try {
            setLoading(true);
            const response = await serverClient.get('/api/user');
            setUser(response?.data?.user);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, t]);

    useEffect(() => {
        feather.replace();
    }, []);

    // Only fetch on initial mount
    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Fetch when refresh signal changes (skips initial mount)
    useRefreshSignal(refreshSignal, () => {
        fetchUser();
    });

    return (
        <>
            <Helmet>
                <title>{getPageMeta('profile', language).title}</title>
                <meta name="description" content={getPageMeta('profile', language).description} />
                <meta name="keywords" content={getPageMeta('profile', language).keywords} />
                <link rel="canonical" href={getPageMeta('profile', language).url} />
                {getPageMeta('profile', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('profile', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('profile', language).description}
                />
                <meta property="og:url" content={getPageMeta('profile', language).url} />
                <meta property="og:image" content={getPageMeta('profile', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('profile', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('profile', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('profile', language).image} />

                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'WebPage',
                        name:
                            language === 'bn'
                                ? 'Xpenso ব্যবহারকারী প্রোফাইল'
                                : 'Xpenso User Profile',
                        description: getPageMeta('profile', language).description,
                        url: getPageMeta('profile', language).url,
                        image: getPageMeta('profile', language).image,
                    })}
                </script>
            </Helmet>
            <div className="profile-page">
                <Loader loading={loading} />

                <ProfileHeader />
                {error && (
                    <div className="p-4">
                        <Alert alert={'danger'}>{error}</Alert>
                    </div>
                )}

                {user && <ProfileSection user={user} error={error} />}
                <BottomNavbar />
            </div>
        </>
    );
}

export default Profile;
